// TODO: add i18n

import type { AutocompleteOptions } from '@libs/ui/ds'
import {
  Autocomplete,
  BinIcon,
  Checkbox,
  CloseIcon,
  IconButton,
  IconWrap,
  Tooltip
} from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import type { ChangeEvent, FC, MouseEvent } from 'react'
import React from 'react'

import styles from './MealRow.module.css'

export type MealValue = {
  id?: number | string | null
  initialMeal?: string
  meal?: any
  checked?: boolean
}

export type MealRowProps = {
  value?: MealValue
  disabled?: boolean
  inputName?: string
  defaultSelectedOption?: string
  mealTypesOptions: AutocompleteOptions[]
  onChange?: (newValue: MealValue) => void
  onRemove?: (evt: MouseEvent<HTMLButtonElement>) => void
  isManager: boolean
  isShiftLocked?: boolean
  real?: boolean
  canEmployeeEdit: boolean
}

export const MealRow: FC<MealRowProps> = ({
  value,
  inputName,
  mealTypesOptions,
  disabled = false,
  isShiftLocked = false,
  real = false,
  onRemove,
  onChange,
  isManager,
  canEmployeeEdit,
  defaultSelectedOption
}) => {
  const { t } = usePlanningFormTranslation()

  const NON_TAKEN_OPTION = [
    {
      value: 'Repas non pris',
      label: t('component.mealRow.nonTakenMeal'),
      icon: <IconWrap icon={<CloseIcon />} size='xs' />
    }
  ]

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target
    if (onChange && value) {
      // if no previous planned meal, initial meal will be `undefined`
      const hadPlannedMeal = typeof value.initialMeal !== 'undefined'
      // so we fallback to the default option
      const mealType = !hadPlannedMeal
        ? defaultSelectedOption
        : value.initialMeal

      const meal = mealTypesOptions.find(options => options.value === mealType)

      onChange({
        id: value.id,
        checked,
        initialMeal: mealType,
        meal
      })
    }
  }

  const handleMealChange = newMeal => {
    const newMealReal = mealTypesOptions.find(x => x.value === newMeal)

    if (onChange && value && newMealReal) {
      onChange({
        id: value.id,
        checked: value.checked,
        initialMeal: value.initialMeal,
        meal: newMealReal
      })
    }
  }

  const getTooltipLabel = () => {
    if (isShiftLocked) {
      return t('component.mealRow.lockAction')
    }
    if (typeof value?.id !== 'number') {
      return t('component.mealRow.removeTooltip')
    }
    if (
      !canEmployeeEdit &&
      !isManager &&
      !isShiftLocked &&
      typeof value?.id === 'number'
    ) {
      return t('component.mealRow.disableModify')
    }

    if (
      (canEmployeeEdit || isManager) &&
      !isShiftLocked &&
      value?.checked &&
      typeof value?.id === 'number'
    ) {
      return t('component.mealRow.removeMeal')
    }

    if (
      (canEmployeeEdit || isManager) &&
      !isShiftLocked &&
      !value?.checked &&
      typeof value?.id === 'number'
    ) {
      return t('component.mealRow.readdMeal')
    }
    return ''
  }

  return (
    <div className={styles.GroupMealRow}>
      <div className='grid grid-cols-12 items-center justify-items-start gap-2'>
        {real && (
          <Tooltip
            id='real_meal_tooltip'
            position='top'
            label={getTooltipLabel()}
          >
            <Checkbox
              size='m'
              name={`${inputName}.checked`}
              checked={value?.checked}
              onChange={handleCheckboxChange}
              disabled={
                (!canEmployeeEdit && !isManager) ||
                isShiftLocked ||
                typeof value?.id !== 'number'
              }
            />
          </Tooltip>
        )}
        <div className={`${real ? 'col-span-10' : 'col-span-11'} w-full`}>
          {value?.checked ? (
            <Autocomplete
              dropdownHeight='m'
              dropdownWidth='target'
              onChange={handleMealChange}
              options={mealTypesOptions}
              value={value?.meal.value}
            />
          ) : (
            <Autocomplete
              options={NON_TAKEN_OPTION}
              value={NON_TAKEN_OPTION[0].value}
              disabled
            />
          )}
        </div>

        {onRemove &&
          isManager &&
          !disabled &&
          (typeof value?.id !== 'number' || !real) && (
            <div className={styles.TrashButton}>
              <Tooltip
                id='delete_tooltip'
                position='right'
                label={t('component.mealRow.delete')}
              >
                <IconButton
                  variant='tertiary'
                  intent='danger'
                  size='m'
                  icon={<BinIcon />}
                  onClick={onRemove}
                />
              </Tooltip>
            </div>
          )}
      </div>
    </div>
  )
}
